import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  useCanViewPersonCareerBool,
  useCanViewPersonDocumentsBool,
  useCanViewPersonMembershipBool
} from "@/hooks/useCapabilities";
import { Person } from "@/types";
import { PERSON_NAME_CHANGE_DOCUMENT_QUERY } from "@/features/person/queries";
import { usePersonContactAccess } from "./_usePersonContactAccess";
import { PERSON_CONTACT_MEMBERSHIP_QUERY, PERSON_CONTACT_CAREER_QUERY, PERSON_DETAILS_QUERY } from "./queries";

type PersonData = { person: Person };

export function usePersonContactDetailsQuery(pgaId?: string) {
  const { loaded, canViewPersonContact } = usePersonContactAccess();
  const canViewPersonMembership = useCanViewPersonMembershipBool();
  const canViewPersonCareer = useCanViewPersonCareerBool();
  const canViewPersonDocuments = useCanViewPersonDocumentsBool();

  const skip = !loaded || !canViewPersonContact || !pgaId;
  const { data, error } = useQuery<PersonData>(PERSON_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip
  });
  const { data: membershipData, loading: membershipLoading } = useQuery(PERSON_CONTACT_MEMBERSHIP_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    skip: skip || !canViewPersonMembership,
    fetchPolicy: "network-only"
  });

  const { data: careerData, loading: careerLoading } = useQuery(PERSON_CONTACT_CAREER_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    skip: skip || !canViewPersonCareer,
    fetchPolicy: "network-only"
  });

  const { data: nameChangeDocumentData, loading: nameChangeDocumentLoading } = useQuery(
    PERSON_NAME_CHANGE_DOCUMENT_QUERY,
    {
      variables: { pgaId: pgaId ?? "" },
      skip: skip || !canViewPersonDocuments,
      fetchPolicy: "network-only"
    }
  );

  const membershipReady = !canViewPersonMembership || !membershipLoading;
  const careerReady = !canViewPersonCareer || !careerLoading;
  const nameChangeDocumentReady = !canViewPersonDocuments || !nameChangeDocumentLoading;
  const detailsPerson = data?.person;
  const membershipPerson = membershipData?.person;
  const careerPerson = careerData?.person;
  const nameChangeDocumentPerson = nameChangeDocumentData?.person;
  // Keep the assembled object referentially stable so consumers can sync on identity
  // without refires on incidental parent re-renders.
  const person = useMemo<Person | undefined>(() => {
    if (!detailsPerson || !membershipReady || !careerReady || !nameChangeDocumentReady) return undefined;
    return {
      ...detailsPerson,
      ...(membershipPerson ? { latestProgram: membershipPerson.latestProgram } : {}),
      ...(careerPerson?.employments !== undefined ? { employments: careerPerson.employments } : {}),
      ...(nameChangeDocumentPerson
        ? { nameChangeDocument: nameChangeDocumentPerson.nameChangeDocument ?? undefined }
        : {})
    };
  }, [
    detailsPerson,
    membershipReady,
    careerReady,
    nameChangeDocumentReady,
    membershipPerson,
    careerPerson,
    nameChangeDocumentPerson
  ]);

  return { loaded, canViewPersonContact, person, error };
}
